import { route } from 'quasar/wrappers'
import { createRouter, createMemoryHistory, createWebHistory, createWebHashHistory } from 'vue-router'
import routes from './routes'
import { useMainStore } from 'src/stores/main'
import { i18n } from 'src/boot/i18n'
import { isChunkLoadError } from 'src/services/error-handling'

const SUPPORTED_LOCALES = Object.freeze({
  en: 'en-us',
  'en-us': 'en-us',
  it: 'it-it',
  'it-it': 'it-it'
})

function normalizeLangQuery(rawLang) {
  const value = Array.isArray(rawLang) ? rawLang[0] : rawLang
  if (typeof value !== 'string') {
    return null
  }

  return SUPPORTED_LOCALES[value.toLowerCase()] ?? null
}

function extractLangFromNextQuery(rawNext) {
  const nextValue = Array.isArray(rawNext) ? rawNext[0] : rawNext
  if (typeof nextValue !== 'string') {
    return null
  }

  try {
    const nextUrl = new URL(nextValue, 'http://localhost')
    return normalizeLangQuery(nextUrl.searchParams.get('lang'))
  } catch {
    return null
  }
}

function setGlobalLocale(locale) {
  const globalLocale = i18n.global.locale
  if (typeof globalLocale === 'string') {
    i18n.global.locale = locale
    return
  }

  globalLocale.value = locale
}

export default route(function () {
  const createHistory = import.meta.env.VITE_SERVER
    ? createMemoryHistory
        : import.meta.env.VITE_VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    history: createHistory(
      import.meta.env.VITE_VUE_ROUTER_BASE
    )
  })

  Router.beforeEach((to, from, next) => {
    const auth = useMainStore()
    const langFromQuery = normalizeLangQuery(to.query.lang)
    const inheritedLang =
      langFromQuery ??
      normalizeLangQuery(from.query.lang) ??
      extractLangFromNextQuery(to.query.next)

    if (inheritedLang) {
      setGlobalLocale(inheritedLang)
    }

    if (to.meta.requiresAuth && !auth.isLoggedIn) {
      const loginQuery = {
        next: to.fullPath
      }
      if (inheritedLang) {
        loginQuery.lang = inheritedLang
      }

      next({
        path: '/login',
        query: loginQuery
      })
    } else {
      next()
    }
  })

  Router.afterEach((to) => {
    window.sessionStorage.removeItem(`chunk-reload:${to.fullPath}`)
  })

  Router.onError((error, to) => {
    if (!isChunkLoadError(error)) {
      return
    }

    const reloadKey = `chunk-reload:${to.fullPath}`
    if (window.sessionStorage.getItem(reloadKey) === '1') {
      window.sessionStorage.removeItem(reloadKey)
      return
    }

    window.sessionStorage.setItem(reloadKey, '1')
    window.location.assign(to.fullPath)
  })

  return Router
})
